// The stage's dimensional language — every device-variant geometry and type
// value, in CANVAS px (see CONTEXT.md: ADR-2, proportional canvas scaling).
// One interface, one adapter per device: the conductor and the scene
// components consume a StageConfig and never hardcode geometry literals.
//
// Derived values (clip line, strip positions, copy anchors) are computed
// from these — never restate them by hand.

export interface StageConfig {
	canvas: {
		/** reference design width (Figma frame) — all values below are px at this width */
		refWidth: number;
		/** minimum vertical room the composition needs (height guard) */
		minHeight: number;
		/**
		 * The first-fold column's natural height (px at refWidth). When the viewport
		 * is SHORTER than this, the canvas scales DOWN to fit it — so the heading /
		 * card / logos never overlap (and never need scroll) when browser chrome /
		 * a dock shrinks the usable height. Viewports taller than this are
		 * unaffected. Distinct from `minHeight` (which only restrains zooming UP).
		 */
		fitHeight: number;
		/** viewport width past which the design stops zooming up */
		maxScaleAtWidth: number;
		/** viewport width below which the layout freezes + center-crops */
		minScaleAtWidth: number;
		/**
		 * Hard cap on the canvas width (px). Past this viewport width the stage
		 * stops widening and stays CENTERED — the section's gradient shows in the
		 * left/right margins (letterbox). Omit to always cover the viewport.
		 */
		maxCanvasWidth?: number;
	};
	/** pinned scroll runway, in viewport-heights */
	pinScreens: number;
	type: {
		/** hero h1 */
		heading: number;
		/** hero subtitle */
		subtitle: number;
		/** LIFETIME FREE background word */
		word: number;
		/** benefit copy titles (0 Forex / 1% Fuel) */
		benefitTitle: number;
		/** 10% badge value (deal + hotel scenes) */
		dealValue: number;
	};
	/** hero credit-card visual; height follows the image's natural aspect */
	card: { w: number; h: number };
	/** dotted border: drawn around the card, then enlarged around the stage */
	dottedFrame: { initW: number; initH: number; bigW: number; bigH: number };
	/** three-station carousel centre frame (caption box shares it) */
	stage: { w: number; h: number };
	strips: {
		smallW: number;
		smallH: number;
		/** gap between strip cards */
		gap: number;
		/** clip line sits this far outside the enlarged dotted border */
		clearance: number;
		/** parked cards sit this far inside the clip line (fully hidden) */
		tuckInset: number;
	};
	/** gap between the card's edge and the benefit copy blocks */
	copyGap: number;
	logos: { w: number; h: number };
	/** "No Gimmicks" heading top offset */
	gimmicksTop: number;
}

/** x of the strip clip line from canvas centre (cards vanish/appear here) */
export function clipLineX(cfg: StageConfig): number {
	return cfg.dottedFrame.bigW / 2 + cfg.strips.clearance;
}

/** x-offset of strip position p (0 = nearest the frame, flush at the line) */
export function stripX(cfg: StageConfig, p: number): number {
	const { smallW, gap } = cfg.strips;
	return clipLineX(cfg) + smallW / 2 + p * (smallW + gap);
}

/** parked-card x — just past the clip line, fully hidden */
export function tuckX(cfg: StageConfig): number {
	return clipLineX(cfg) - cfg.strips.smallW / 2 - cfg.strips.tuckInset;
}

/** distance from canvas centre to the benefit copy's inner edge */
export function copyAnchorX(cfg: StageConfig): number {
	return cfg.card.w / 2 + cfg.copyGap;
}

export const WEB_STAGE_CONFIG: StageConfig = {
	canvas: {
		refWidth: 1440,
		minHeight: 900,
		// First-fold column (heading→subtitle→CTA→card→logos) is ~660px tall at
		// 1440; 760 leaves margin. Below this usable height the canvas scales DOWN
		// so nothing overlaps (the "sticks together on short viewports / extra
		// browser chrome" fix) — no scroll needed.
		fitHeight: 760,
		// Scale ceiling = 1920 ⇒ the design may zoom UP to 1.333× (1920/1440) on
		// larger screens, mirroring how it scales DOWN below 1440. TRIAL (was 1440
		// = no scale-up): scaling up upscales the raster assets (card art, carousel
		// photos, the 1600px video frames) — fine to ~1.2× (FHD), softer toward
		// 1.333×. Drop back to 1440 if the assets look bad before higher-res ones land.
		maxScaleAtWidth: 1920,
		// Floor at 1024 (our min supported web width), NOT 1120: below this the
		// layout freezes and the viewport CENTER-CROPS it. At 1120 a 1024px window
		// lost ~48px off each side, clipping the edge-anchored chrome (logo,
		// header CTA, left benefit heading). 1024 makes the canvas fit-to-width
		// with zero crop across the whole 1024–1600 range.
		minScaleAtWidth: 1024,
		// Canvas width cap = 1920 (raised from 1680). With scale-up to 1.333×, the
		// stage FILLS the viewport up to ~2560×1440 (QHD); past that the canvas
		// holds 1920 wide, centers, and the section gradient letterboxes the sides.
		maxCanvasWidth: 1920,
	},
	// ~1 viewport-height per timeline unit. The lock-and-play video transition is
	// now 4 clips totalling 30 units: window→room (clip 0, 5u), the merged
	// hotel→bus leg (clip 1, 10u = room→hallway→bus), bus-interior push-in
	// (clip 2, 5u), then the merged train scene (clip 3, 10u = bridge→interior).
	// pinScreens moves in LOCKSTEP with total clip units so px-per-unit stays ~1:
	// 24 for the original 4-clip (20-unit) journey, +5 per 5 added units → 34 at
	// 30 units. Merging two adjacent clips keeps the SUM of units (so pinScreens is
	// unchanged); it only removes an internal seam.
	pinScreens: 34,
	type: {
		heading: 72,
		subtitle: 18,
		word: 150, // = 11vw at the 1440 canvas (vw can't be used inside the canvas)
		benefitTitle: 42,
		dealValue: 180,
	},
	card: { w: 240, h: 379 },
	// init size wraps the card (240×379) with a 15px gap per side; bigW/bigH is the
	// enlarged frame that wraps the carousel stage (480×720) in phase 4.
	dottedFrame: { initW: 270, initH: 409, bigW: 526, bigH: 773 },
	stage: { w: 480, h: 720 },
	strips: { smallW: 160, smallH: 240, gap: 28, clearance: 20, tuckInset: 10 },
	copyGap: 90,
	logos: { w: 200, h: 20 },
	gimmicksTop: 140,
};

// TODO(mweb): fill from the Figma mweb frames (375×812, node 3128-8444).
// Placeholder values = web's, so the seam exists and type-checks; the mweb
// journey ships by replacing these numbers (and nothing else).
export const MWEB_STAGE_CONFIG: StageConfig = {
	...WEB_STAGE_CONFIG,
	canvas: {
		refWidth: 390,
		minHeight: 700,
		// mobile viewports are tall, so this rarely binds; a safe floor matching
		// minHeight so a very short mobile (landscape) still fits without overlap.
		fitHeight: 700,
		maxScaleAtWidth: 430,
		minScaleAtWidth: 360,
	},
	// mweb showcase stage — the "big screen" carousel frame (no side strips on
	// mobile), portrait. The DRAW centres on the card seat; the ENLARGED frame
	// re-anchors 10px below the "No Catch." heading (see the showcase beat), so
	// this rect only needs to fit the width and the footer clearance.
	// TODO(figma): confirm against the mweb frames.
	stage: { w: 350, h: 440 },
};

export function stageConfigFor(device: 'web' | 'mweb'): StageConfig {
	return device === 'web' ? WEB_STAGE_CONFIG : MWEB_STAGE_CONFIG;
}
